import React, {
  memo,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";

import _debounce from "lodash.debounce";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, FormLabel, Grid, IconButton } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ArrowCircleRightRoundedIcon from "@mui/icons-material/ArrowCircleRightRounded";

import SuiInputV2 from "components/SuiInputV2";
import SuiTypography from "components/SuiTypography";
import toSnakeCase from "utils";

const PRIMARY_KEYS = ["first_name", "last_name", "email", "phone", "additional_details"];

const CustomEndpointsV2 = forwardRef(
  ({ customEndpoints, setCustomEnpoints }, ref) => {
    const [usedLabels, setUsedLabels] = useState(new Set());
    const [usedLookupKeys, setUsedLookupKeys] = useState(new Set());

    // Validation state
    const [errors, setErrors] = useState([]);
    const [touched, setTouched] = useState([]);

    const onChangeHandler = (index, field, value) => {
      const newFields = [...customEndpoints];
      const oldValue = newFields[index][field];
      newFields[index] = {
        ...newFields[index],
        [field]: value,
      };

      // Update used sets for uniqueness validation
      if (field === "label") {
        const newLabels = new Set(usedLabels);

        if (oldValue) {
          newLabels.delete(oldValue.toLowerCase());
        }
        if (value) {
          newLabels.add(value.toLowerCase());
        }

        setUsedLabels(newLabels);
      } else if (field === "lookupKey") {
        const newKeys = new Set(usedLookupKeys);

        if (oldValue) {
          newKeys.delete(oldValue.toLowerCase());
        }
        if (value) {
          newKeys.add(value.toLowerCase());
        }

        setUsedLookupKeys(newKeys);
      }

      setCustomEnpoints(newFields);

      // Mark field as touched when typing begins
      const newTouched = { ...touched };
      if (!newTouched[index]) {
        newTouched[index] = {};
      }
      if (!newTouched[index][field]) {
        newTouched[index][field] = true;
        setTouched(newTouched);
      }

      validateField(index, field, value);
    };

    const onBlurHandler = (index, field) => {
      // Mark field as touched
      const newTouched = { ...touched };
      newTouched[index] = newTouched[index] || {};
      newTouched[index][field] = true;
      setTouched(newTouched);

      validateField(index, field, customEndpoints[index][field]);
    };

    // Improved conversion to lookup key with validation
    function onClickConvertToLookupKey({ field, index }) {
      const snakeCaseLookupKey = toSnakeCase(field.label);

      // Check for duplicate lookup keys
      const isDuplicateLookupKey = customEndpoints.some(
        (endpoint, i) =>
          i !== index &&
          endpoint.lookupKey.toLowerCase() === snakeCaseLookupKey.toLowerCase()
      );

      if (isDuplicateLookupKey) {
        // Add error for duplicate lookup key
        const newErrors = { ...errors };
        if (!newErrors[index]) newErrors[index] = {};
        newErrors[index].lookupKey = "Lookup Key already exists!";
        setErrors(newErrors);
        return;
      }

      // Update lookup key if no duplicate
      setCustomEnpoints((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, lookupKey: snakeCaseLookupKey } : item
        )
      );

      // Mark lookupKey as touched and validate
      const newTouched = { ...touched };
      if (!newTouched[index]) newTouched[index] = {};
      newTouched[index].lookupKey = true;
      setTouched(newTouched);
      validateField(index, "lookupKey", snakeCaseLookupKey);
    }

    const addRow = () => {
      // if (customEndpoints.length >= 20) return;

      // Check if the last row has valid data
      const lastIndex = customEndpoints.length - 1;
      const lastRow = customEndpoints[lastIndex];

      // Validate all fields of the last row
      const newErrors = { ...errors };
      let hasErrors = false;

      ["label", "lookupKey", "description"].forEach((field) => {
        if (!lastRow[field]?.trim()) {
          newErrors[lastIndex] = newErrors[lastIndex] || {};
          newErrors[lastIndex][field] = `${
            field === "lookupKey" ? "Lookup Key" : field
          } can't be empty`;
          hasErrors = true;
        }
      });

      // Mark all fields as touched
      const newTouched = { ...touched };
      newTouched[lastIndex] = {
        label: true,
        lookupKey: true,
        description: true,
      };
      setTouched(newTouched);

      // Update errors
      setErrors(newErrors);

      // Only allow adding a new row if there are no errors
      if (!hasErrors) {
        setCustomEnpoints([
          ...customEndpoints,
          { label: "", lookupKey: "", description: "", required: false },
        ]);
      }
    };

    const removeRow = (index) => {
      if (customEndpoints.length > 1) {
        const newFields = [...customEndpoints];
        const removedField = newFields[index];

        // Remove from used sets if not empty
        if (removedField.label) {
          const newLabels = new Set(usedLabels);
          newLabels.delete(removedField.label.toLowerCase());
          setUsedLabels(newLabels);
        }

        if (removedField.lookupKey) {
          const newKeys = new Set(usedLookupKeys);
          newKeys.delete(removedField.lookupKey.toLowerCase());
          setUsedLookupKeys(newKeys);
        }

        newFields.splice(index, 1);
        setCustomEnpoints(newFields);
      }
    };

    const validateField = useCallback(
      _debounce((index, field, value) => {
        const newErrors = { ...errors };

        if (!newErrors[index]) newErrors[index] = {};

        // Check for empty value
        if (!value.trim()) {
          newErrors[index][field] =
            field === "label"
              ? "Label can't be empty"
              : field === "lookupKey"
              ? "Lookup Key can't be empty"
              : "Description can't be empty";
        } else {
          // Check for duplicates based on field
          if (field === "label") {
            const isDuplicateLabel = customEndpoints.some(
              (endpoint, i) => i !== index && endpoint.label === value
            );

            if (isDuplicateLabel) {
              newErrors[index][field] = "Label already exists!";
            } else {
              delete newErrors[index][field];
            }
          } else if (field === "lookupKey") {
            const isDuplicateLookupKey = customEndpoints.some(
              (endpoint, i) =>
                i !== index &&
                endpoint.lookupKey.toLowerCase() === value.toLowerCase()
            );

            if (isDuplicateLookupKey) {
              newErrors[index][field] = "Lookup Key already exists!";
            } else {
              delete newErrors[index][field];
            }
          } else {
            delete newErrors[index][field];
          }
        }

        setErrors(newErrors);
      }, 300),
      [errors]
    );

    const isPrimaryField = (lookupKey) => PRIMARY_KEYS.includes(lookupKey);

    useImperativeHandle(ref, () => ({
      isValidInputs() {
        return Object.values(errors).every(
          (value) => Object.keys(value).length === 0
        );
      },
    }));

    return (
      <Accordion
        sx={{
          border: "1px solid #EEEFF1",
          borderRadius: "5px",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="customEndpoints-content"
          id="customEndpoints-header"
          sx={{
            height: "70px",
            minHeight: "70px",
            "&.Mui-expanded": {
              minHeight: "70px",
              height: "70px",
            },
          }}
        >
          <Typography fontSize="14px" fontWeight="semiBold">
            Custom End Points
          </Typography>
        </AccordionSummary>

        <AccordionDetails
          sx={{ p: 0, pt: 1, mb: 2, borderTop: "2px solid #E4E9F0" }}
        >
          <Box>
            {/* Headers */}
            <Grid container sx={{ px: 2, py: 1 }}>
              <Grid item xs={3} sx={{ px: 1 }}>
                <FormLabel required>Label</FormLabel>
              </Grid>
              <Grid item xs={4} sx={{ px: 1 }}>
                <FormLabel required>Lookup Key</FormLabel>
              </Grid>
              <Grid item xs={4} sx={{ px: 1 }}>
                <FormLabel required>Description</FormLabel>
              </Grid>
              <Grid item xs={1} />
            </Grid>

            {customEndpoints.map((field, index) => (
              <Grid container key={`custom-${index}`} sx={{ px: 2, py: 1 }}>
                <Grid item xs={3} sx={{ px: 1 }}>
                  <SuiInputV2
                    id={`custom-label-${index}`}
                    name={`custom-label-${index}`}
                    placeholder="Enter label"
                    value={field.label}
                    onChange={(e) =>
                      onChangeHandler(index, "label", e.target.value)
                    }
                    onBlur={() => onBlurHandler(index, "label")}
                    error={
                      touched?.[index]?.label
                        ? errors?.[index]?.label
                        : undefined
                    }
                    touched={touched?.[index]?.label}
                    InputProps={{
                      endAdornment: !isPrimaryField(field.lookupKey) && (
                        <InputAdornment
                          position="end"
                          sx={{
                            position: "absolute",
                            right: "7px",
                            fontSize: "20px",
                            cursor: "pointer",
                          }}
                        >
                          <ArrowCircleRightRoundedIcon
                            onClick={() =>
                              onClickConvertToLookupKey({ field, index })
                            }
                            color="black"
                          />
                        </InputAdornment>
                      ),
                    }}
                    inputSettings={{
                      sx:
                        touched?.[index]?.label && errors?.[index]?.label
                          ? { border: "1px solid #ff3d57" }
                          : {},
                    }}
                    customSx={{
                      "& .MuiOutlinedInput-root input": {
                        paddingRight: "30px",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={3} sx={{ px: 1 }}>
                  <SuiInputV2
                    id={`custom-lookupKey-${index}`}
                    name={`custom-lookupKey-${index}`}
                    placeholder="Enter lookup key"
                    value={field.lookupKey}
                    onChange={(e) =>
                      onChangeHandler(index, "lookupKey", e.target.value)
                    }
                    disabled={
                      isPrimaryField(field.lookupKey) &&
                      !errors?.[index]?.lookupKey
                    }
                    onBlur={() => onBlurHandler(index, "lookupKey")}
                    error={
                      touched?.[index]?.lookupKey
                        ? errors?.[index]?.lookupKey
                        : undefined
                    }
                    touched={touched?.[index]?.lookupKey}
                    inputSettings={{
                      sx:
                        touched?.[index]?.lookupKey &&
                        errors?.[index]?.lookupKey
                          ? { border: "1px solid #ff3d57" }
                          : {},
                    }}
                    customSx={{
                      "& .MuiOutlinedInput-root input": {
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={5.2} sx={{ px: 1 }}>
                  <SuiInputV2
                    id={`custom-description-${index}`}
                    name={`custom-description-${index}`}
                    placeholder="Enter description"
                    value={field.description}
                    onChange={(e) =>
                      onChangeHandler(index, "description", e.target.value)
                    }
                    onBlur={() => onBlurHandler(index, "description")}
                    error={
                      touched?.[index]?.description
                        ? errors?.[index]?.description
                        : undefined
                    }
                    touched={touched?.[index]?.description}
                    inputSettings={{
                      sx:
                        touched?.[index]?.description &&
                        errors?.[index]?.description
                          ? { border: "1px solid #ff3d57" }
                          : {},
                    }}
                    customSx={{
                      "& .MuiOutlinedInput-root input": {
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={0.8}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    marginTop: "3px",
                  }}
                >
                  {!isPrimaryField(field.lookupKey) && (
                    <IconButton
                      size="small"
                      sx={{
                        backgroundColor: "#ff3d57",
                        borderRadius: "4px",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "#d32f2f",
                        },
                        width: "30px",
                        height: "30px",
                        minWidth: "30px",
                      }}
                      onClick={() => removeRow(index)}
                    >
                      <RemoveIcon color="white" fontSize="small" />
                    </IconButton>
                  )}

                  {index === customEndpoints.length - 1 && (
                    <IconButton
                      size="small"
                      sx={{
                        backgroundColor: "#1e88e5",
                        borderRadius: "4px",
                        color: "white",
                        ml: 0.5,
                        "&:hover": {
                          backgroundColor: "#1565c0",
                        },
                        width: "30px",
                        height: "30px",
                        minWidth: "30px",
                      }}
                      onClick={addRow}
                      // disabled={customEndpoints.length >= 20} note: disabling the btn removes visibility which is intended
                    >
                      <AddIcon color="white" fontSize="small" />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            ))}
            {/* Footer note */}
            <SuiTypography textColor="text2" fontSize={15} pt={1} mx={3}>
              *The initial parameters, such as first name, last name, email,
              phone number and additional details are predefined and function as
              primary fields.
            </SuiTypography>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  }
);

export default memo(CustomEndpointsV2);
